import { merge } from '../../helper';

import exLanguage from '../../language';

import * as exModal from './modal';
import type { IModalConfig } from './types';

// ----------------------------------------------------------------------------

export interface IModalConfirm extends IModalConfig {
    type?: string;
    title?: string;
    message?: string;
    textAction?: string;
    classAction?: string;
    fnAction?: any;
    textCancel?: string;
    classCancel?: string;
    fnCancel?: any;
}

// ----------------------------------------------------------------------------

function getConfirmButtons(config: IModalConfirm = {}): any[] {
    return [
        {
            html: config.textCancel ?? exLanguage.line('general.cancel'),
            class: {
                new: config.classCancel ?? 'btn btn-secondary',
            },
            fnAction: config.fnCancel,
        },
        {
            html: config.textAction ?? exLanguage.line('general.confirm'),
            class: {
                new: config.classAction ?? 'btn btn-primary',
            },
            fnAction: config.fnAction,
        },
    ];
}

function getConfirmConfig(config: IModalConfirm = {}): Record<string, any> {
    return {
        modalHeader: {
            type: config.type,
            title: {
                html: config.title ?? exLanguage.line('general.confirm'),
            },
        },
        modalBody: {
            html: config.message ?? exLanguage.line('modal.message'),
        },
        buttons: getConfirmButtons(config),
    };
}

// ----------------------------------------------------------------------------

function confirm(config: IModalConfirm = {}): Record<string, any> {
    const defConfig: Record<string, any> = getConfirmConfig(config);
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    // Create element (modal)
    const modal = exModal.start({
        merge: defConfig,
        destroy: true,
    });

    // ------------------------------------------------------------------------

    // Return
    return modal;
}

// ----------------------------------------------------------------------------

async function confirmAsync(config: IModalConfirm = {}): Promise<boolean> {
    return new Promise((resolve): any => {
        const defConfig: Record<string, any> = getConfirmConfig(config);
        merge(defConfig, config);

        // --------------------------------------------------------------------

        let isResolved = false;

        const resolveOnce = (result: boolean): void => {
            if (isResolved) {
                return;
            }

            isResolved = true;
            resolve(result);
        };

        // --------------------------------------------------------------------

        const buttons = getConfirmButtons(defConfig);

        buttons[0].fnAction = async function (evt): Promise<void> {
            await config.fnCancel?.(evt);
            resolveOnce(false);
        };
        buttons[0].closeOnAction = true;

        buttons[1].fnAction = async function (evt): Promise<void> {
            await config.fnAction?.(evt);
            resolveOnce(true);
        };
        buttons[1].closeOnAction = true;

        defConfig.buttons = buttons;

        // --------------------------------------------------------------------

        const fnHidden = defConfig.modalEvent?.hidden;

        defConfig.modalEvent = {
            ...defConfig.modalEvent,
            hidden: function (evt): void {
                fnHidden?.(evt);
                resolveOnce(false);
            },
        };

        // --------------------------------------------------------------------

        exModal.start({
            merge: defConfig,
            destroy: true,
        });
    });
}

// ----------------------------------------------------------------------------

export { confirm, confirmAsync };
